import React, { useRef } from "react";
import {
  DataTable,
  DataTableExpandedRows,
  DataTableRowEvent,
} from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { confirmDialog } from "primereact/confirmdialog";
import { DocumentItem } from "./DocumentLayout";
import DocumentService from "../../services/DocumentService";
import { Tooltip } from "primereact/tooltip";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";

type Props = {
  documents: DocumentItem[];
  loading: boolean;
  onDelete: (row: DocumentItem) => void;
};

const DocumentTable: React.FC<Props> = ({ documents, loading, onDelete }) => {
  const toast = useRef<Toast>(null);
  const [expandedRows, setExpandedRows] = React.useState<
    DataTableExpandedRows | undefined
  >(undefined);
  const [deleting, setDeleting] = React.useState<number | null>(null); // ✅ Track currently deleting row

  // ✅ Confirm before delete
  const onDeleteClick = (rowData: DocumentItem) => {
    confirmDialog({
      message: `Are you sure you want to delete "${rowData.pdfName}"?`,
      header: "Confirm Delete",
      icon: "pi pi-exclamation-triangle",
      accept: () => onConfirmDelete(rowData),
    });
  };

  // ✅ Delete API + UI update
  const onConfirmDelete = async (rowData: DocumentItem) => {
    try {
      setDeleting(rowData.pdfId);

      // 🔹 Call backend delete API
      const res: any = await DocumentService.DeletePDF({
        pdfid: rowData.pdfId,
        userid: rowData.userId,
      });

      // ✅ Handle delete response more gracefully
      if (res?.status === 200 || res?.success === true) {
        toast.current?.show({
          severity: "success",
          summary: "Deleted",
          detail: `${rowData.pdfName} deleted successfully.`,
          life: 3000,
        });
        onDelete(rowData);
      } else if (
        res?.p_msg?.toLowerCase().includes("already deleted") ||
        res?.p_response === false
      ) {
        // 🔹 Handle "already deleted" case silently
        toast.current?.show({
          severity: "info",
          summary: "Already Deleted",
          detail: `${rowData.pdfName} was already deleted.`,
          life: 3000,
        });
        onDelete(rowData); // still remove from UI
      } else {
        throw new Error("Delete failed");
      }
    } catch (error) {
      console.error("Delete error:", error);
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Something went wrong while deleting.",
        life: 3000,
      });
    } finally {
      setDeleting(null);
    }
  };

  // ✅ Action column with loader on delete
  const actionBodyTemplate = (rowData: DocumentItem) => {
    const isDeleting = deleting === rowData.pdfId;

    return (
      <React.Fragment>
        <Button
          className={`table-button custome-icon delete-icon ${
            isDeleting ? "p-disabled" : ""
          }`}
          onClick={(e) => {
            e.stopPropagation(); // ✅ stops bubbling to row click
            if (!isDeleting) onDeleteClick(rowData); // ✅ prevents double API call
          }}
          tooltip={isDeleting ? "Deleting..." : "Delete"}
          icon={isDeleting ? "pi pi-spin pi-spinner" : ""}
        />
      </React.Fragment>
    );
  };

  // ✅ Serial number column
  const SrNo = (_rowData: DocumentItem, options: any) => (
    <div className="flex align-items-center gap-2">
      <span>{options.rowIndex + 1}</span>
    </div>
  );

  // ✅ Date formatting column
  const CreatedDateTemplate = (rowData: DocumentItem) => (
    <div className="flex align-items-center gap-2">
      <span>
        {new Date(rowData.uploadedDate).toLocaleDateString("en-IN", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        })}
      </span>
    </div>
  );

  // ✅ Expandable row details
  const rowExpansionTemplate = (data: DocumentItem) => (
    <div className="p-3">
      <h5>Document Details</h5>
      <p>
        <strong>Document ID:</strong> {data.pdfId}
      </p>
      <p>
        <strong>Uploaded By User ID:</strong> {data.userId}
      </p>
      <p>
        <strong>Uploaded On:</strong>{" "}
        {new Date(data.uploadedDate).toLocaleString()}
      </p>
    </div>
  );

  const onRowExpand = (event: DataTableRowEvent) => {};
  const onRowCollapse = (event: DataTableRowEvent) => {};

  const descriptionTemplate = (rowData: DocumentItem) => {
    const text = rowData.description || "";

    return (
      <span
        className="desc-tooltip"
        data-pr-tooltip={text.length > 25 ? text : ""}
        data-pr-position="top"
        // style={{ cursor: text.length > 25 ? "pointer" : "default" }}
      >
        {text.length > 25 ? text.substring(0, 25) + "..." : text}
      </span>
    );
  };

  const errorIconTemplate = (rowData: DocumentItem) => {
    if (!rowData.errormessage) {
      return null;
    }

    return (
      <>
        <FontAwesomeIcon
          icon={faExclamationTriangle}
          id={`errorIcon_${rowData.pdfId}`}
          style={{ color: "red", cursor: "pointer", fontSize: "1.3rem" }}
        />
        <Tooltip
          target={`#errorIcon_${rowData.pdfId}`}
          content={rowData.errormessage}
        />
      </>
    );
  };

  const blobErrorIconTemplate = (rowData: DocumentItem) => {
    if (!rowData.bloberrormessage) return null;

    return (
      <>
        <FontAwesomeIcon
          icon={faExclamationTriangle}
          id={`blobErrorIcon_${rowData.pdfId}`}
          style={{ color: "red", cursor: "pointer", fontSize: "1.3rem" }}
        />
        <Tooltip
          target={`#blobErrorIcon_${rowData.pdfId}`}
          content={rowData.bloberrormessage}
        />
      </>
    );
  };

  return (
    <div className="table-section">
      <Tooltip target=".desc-tooltip" />
      <Toast ref={toast} />

      <DataTable
        paginator
        rows={15}
        value={documents}
        dataKey="pdfId"
        scrollHeight="600px"
        loading={loading}
        emptyMessage="No documents found."
        expandedRows={expandedRows}
        onRowExpand={onRowExpand}
        onRowCollapse={onRowCollapse}
        rowExpansionTemplate={rowExpansionTemplate}
      >
        <Column
          field="srNo"
          header="Sr.No."
          style={{ minWidth: "3rem" }}
          body={SrNo}
        />
        <Column
          field="pdfName"
          header="Document Name"
          style={{ minWidth: "12rem" }}
          sortable
        />
        <Column
          field="bloberrormessage"
          header="Blob Error"
          body={blobErrorIconTemplate}
          style={{ minWidth: "8rem" }}
        />
        <Column
          field="errormessage"
          header="Embedding Error"
          body={errorIconTemplate}
          style={{ minWidth: "8rem" }}
        />
        <Column
          field="category"
          header="Category"
          style={{ minWidth: "10rem" }}
          sortable
        />
        <Column
          field="description"
          header="Description"
          style={{ minWidth: "10rem" }}
          body={(rowData) => descriptionTemplate(rowData)}
          sortable
        />
        <Column
          field="uploadedDate"
          header="Created Date"
          body={CreatedDateTemplate}
          style={{ minWidth: "10rem" }}
          sortable
        />
        <Column
          field="Action"
          header="Action"
          style={{ minWidth: "6rem" }}
          body={actionBodyTemplate}
        />
      </DataTable>
    </div>
  );
};

export default DocumentTable;
